import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ─── Datos ────────────────────────────────────────────────────────────────────

const DESTINATIONS = [
  {
    id: 'wdw',
    countryFlag: '🇺🇸',
    parkIcon: '🏰',
    label: 'Walt Disney World',
    sublabel: 'Orlando, Florida',
    badge: 'Disney',
    badgeColor: 'from-blue-400 to-cyan-500',
    region: 'usa',
  },
  {
    id: 'dlca',
    countryFlag: '🇺🇸',
    parkIcon: '🏰',
    label: 'Disneyland',
    sublabel: 'Anaheim, California',
    badge: 'Disney',
    badgeColor: 'from-blue-400 to-cyan-500',
    region: 'usa',
  },
  {
    id: 'uso',
    countryFlag: '🇺🇸',
    parkIcon: '🎬',
    label: 'Universal Studios',
    sublabel: 'Orlando, Florida',
    badge: 'Universal',
    badgeColor: 'from-violet-400 to-purple-500',
    region: 'usa',
  },
  {
    id: 'ush',
    countryFlag: '🇺🇸',
    parkIcon: '🎬',
    label: 'Universal Studios',
    sublabel: 'Hollywood, California',
    badge: 'Universal',
    badgeColor: 'from-violet-400 to-purple-500',
    region: 'usa',
  },
  {
    id: 'cruise',
    countryFlag: '🌊',
    parkIcon: '🚢',
    label: 'Crucero Disney',
    sublabel: 'Caribe · Bahamas · Alaska',
    badge: 'Disney Cruise',
    badgeColor: 'from-teal-400 to-emerald-500',
    region: 'usa',
  },
  {
    id: 'dlparis',
    countryFlag: '🇫🇷',
    parkIcon: '🏰',
    label: 'Disneyland París',
    sublabel: 'Chessy, Francia · Europa',
    badge: 'Disney',
    badgeColor: 'from-pink-400 to-rose-500',
    region: 'eu',
  },
];

const BUDGETS = [
  'Menos de $1,000 USD',
  '$1,000 – $2,500 USD',
  '$2,500 – $4,000 USD',
  'Más de $4,000 USD',
  'Aún no tengo definido',
];

const ADVISORY_LEVELS = [
  { id: 'full',    label: 'Completa',     desc: 'Planeación, boletos, itinerario y tips' },
  { id: 'basic',   label: 'Básica',       desc: 'Boletos + reservas principales'         },
  { id: 'tickets', label: 'Solo boletos', desc: 'Únicamente tickets de entrada'          },
];

const TICKET_TYPES = [
  'Disney World',
  'Universal Orlando',
  'Disneyland París',
  'Eventos especiales (Halloween, Navidad, etc.)',
  'Otro',
];

const TRIP_TYPES = ['Familia', 'Pareja', 'Amigos', 'Grupo', 'Otro'];

const EXPERIENCE_TYPES = [
  { id: 'full_pkg', label: 'Paquete completo', desc: 'Hotel + entradas + asesoría personalizada' },
  { id: 'tickets',  label: 'Solo entradas',    desc: 'Únicamente tickets de entrada'             },
  { id: 'cruise',   label: 'Crucero Disney',   desc: 'Experiencia en crucero'                    },
  { id: 'other',    label: 'Otro',             desc: 'Cuéntanos qué necesitas'                   },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const sectionVariants = {
  enter:  { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0  },
  exit:   { opacity: 0, x: -40 },
};

// ─── Sub-componentes ─────────────────────────────────────────────────────────

const OptionCard = ({ selected, onClick, children, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left px-4 py-3 rounded-2xl border-2 transition-all duration-200 font-medium text-sm sm:text-base
      ${selected
        ? 'border-purple-500 bg-purple-50 text-purple-700 shadow-md scale-[1.01]'
        : 'border-gray-200 bg-white text-gray-700 hover:border-purple-300 hover:bg-purple-50/40'}
      ${className}`}
  >
    {children}
  </button>
);

const FieldInput = ({ label, ...props }) => (
  <div className="flex flex-col gap-1">
    {label && <label className="text-sm font-semibold text-gray-600">{label}</label>}
    <input
      {...props}
      className="p-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent text-gray-800 bg-white"
    />
  </div>
);

const FieldTextarea = ({ label, ...props }) => (
  <div className="flex flex-col gap-1">
    {label && <label className="text-sm font-semibold text-gray-600">{label}</label>}
    <textarea
      {...props}
      rows={3}
      className="p-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent text-gray-800 bg-white resize-none"
    />
  </div>
);

const SectionHeader = ({ emoji, title, subtitle }) => (
  <div className="mb-6">
    <div className="text-3xl mb-1">{emoji}</div>
    <h3 className="text-xl font-bold text-gray-800">{title}</h3>
    {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
  </div>
);

const NavButtons = ({ onBack, onNext, nextLabel = 'Continuar →', canNext = true }) => (
  <div className="flex flex-col gap-3 mt-8">
    <motion.button
      type="button"
      onClick={onNext}
      disabled={!canNext}
      whileTap={{ scale: 0.97 }}
      className={`w-full py-4 rounded-2xl font-bold text-white text-base shadow-lg transition-all duration-200
        ${canNext
          ? 'bg-gradient-to-r from-pink-500 to-purple-600 hover:shadow-xl hover:scale-[1.02]'
          : 'bg-gray-300 cursor-not-allowed'}`}
    >
      {nextLabel}
    </motion.button>
    {onBack && (
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
      >
        ← Regresar
      </button>
    )}
  </div>
);

const ProgressBar = ({ step, total }) => (
  <div className="mb-6">
    <div className="flex justify-between text-xs text-gray-400 mb-1">
      <span>Paso {step} de {total}</span>
      <span>{Math.round((step / total) * 100)}%</span>
    </div>
    <div className="w-full bg-gray-100 rounded-full h-2">
      <motion.div
        className="h-2 rounded-full bg-gradient-to-r from-pink-400 to-purple-500"
        initial={{ width: 0 }}
        animate={{ width: `${(step / total) * 100}%` }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      />
    </div>
  </div>
);

// ─── Secciones ────────────────────────────────────────────────────────────────

// S1: Destino
const Section1 = ({ data, set, onNext }) => (
  <>
    <SectionHeader
      emoji="🗺️"
      title="¿A qué destino te gustaría viajar?"
      subtitle="Selecciona el parque o experiencia que más te emociona."
    />
    <div className="flex flex-col gap-3">
      {DESTINATIONS.map((d) => {
        const selected = data.destination === d.id;
        return (
          <motion.button
            key={d.id}
            type="button"
            onClick={() => set('destination', d.id)}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            className={`w-full text-left rounded-2xl border-2 px-4 py-3 transition-all duration-200
              ${selected
                ? 'border-purple-500 bg-purple-50 shadow-md'
                : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50/30'}`}
          >
            <div className="flex items-center gap-3">
              {/* Ícono del parque */}
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-2xl shrink-0
                bg-gradient-to-br ${selected ? d.badgeColor : 'from-gray-100 to-gray-200'}
                transition-all duration-300`}
              >
                {d.parkIcon}
              </div>

              {/* Texto */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`font-bold text-sm sm:text-base ${selected ? 'text-purple-800' : 'text-gray-800'}`}>
                    {d.label}
                  </span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full text-white bg-gradient-to-r ${d.badgeColor}`}>
                    {d.badge}
                  </span>
                </div>
                <div className="flex items-center gap-1 mt-0.5">
                  <span className="text-base leading-none">{d.countryFlag}</span>
                  <span className="text-xs text-gray-500 truncate">{d.sublabel}</span>
                </div>
              </div>

              {/* Check */}
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all duration-200
                ${selected ? 'border-purple-500 bg-purple-500' : 'border-gray-300'}`}
              >
                {selected && (
                  <motion.svg
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-3 h-3 text-white"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </motion.svg>
                )}
              </div>
            </div>
          </motion.button>
        );
      })}
    </div>
    <NavButtons onNext={onNext} canNext={!!data.destination} />
  </>
);

// S2A: Documentación EE.UU.
const Section2A = ({ data, set, onNext, onBack }) => {
  const visaNo     = data.visa === 'no';
  const canContinue = data.passport && data.visa;
  return (
    <>
      <SectionHeader
        emoji="🛂"
        title="Documentación para EE. UU."
        subtitle="Necesitamos verificar que todos los viajeros puedan ingresar al país."
      />

      <div className="mb-6">
        <p className="font-semibold text-gray-700 mb-3">¿Todos cuentan con pasaporte vigente?</p>
        {[
          { val: 'yes',     icon: '✅', label: 'Sí, todos lo tenemos'  },
          { val: 'partial', icon: '⚠️', label: 'Algunos sí, otros no'  },
          { val: 'no',      icon: '❌', label: 'No, ninguno lo tiene'  },
        ].map(o => (
          <OptionCard key={o.val} selected={data.passport === o.val} onClick={() => set('passport', o.val)} className="mb-2">
            <span className="mr-2">{o.icon}</span>{o.label}
          </OptionCard>
        ))}
      </div>

      <div className="mb-2">
        <p className="font-semibold text-gray-700 mb-3">¿Todos cuentan con visa estadounidense aprobada?</p>
        {[
          { val: 'yes',     icon: '✅', label: 'Sí, todos la tenemos'     },
          { val: 'partial', icon: '⚠️', label: 'Algunos sí, otros no'     },
          { val: 'no',      icon: '❌', label: 'No, aún no tenemos visa'  },
        ].map(o => (
          <OptionCard key={o.val} selected={data.visa === o.val} onClick={() => set('visa', o.val)} className="mb-2">
            <span className="mr-2">{o.icon}</span>{o.label}
          </OptionCard>
        ))}
      </div>

      <NavButtons
        onBack={onBack}
        onNext={onNext}
        canNext={canContinue}
        nextLabel={visaNo ? 'Ver información →' : 'Continuar →'}
      />
    </>
  );
};

// S2B: Europa
const Section2B = ({ data, set, onNext, onBack }) => (
  <>
    <SectionHeader
      emoji="🇫🇷"
      title="¡Excelente elección!"
      subtitle="Para Disneyland París no necesitas visa estadounidense, pero sí pasaporte vigente. A partir de 2025 también se requiere permiso ETIAS en algunos países."
    />
    <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6 text-sm text-blue-800">
      ℹ️ El <strong>ETIAS</strong> es un permiso electrónico de viaje para ingresar al espacio Schengen. Es rápido y económico de tramitar en línea.
    </div>
    <p className="font-semibold text-gray-700 mb-3">¿Cuentan con pasaporte vigente?</p>
    {[
      { val: 'yes',     icon: '✅', label: 'Sí, todos'       },
      { val: 'partial', icon: '⚠️', label: 'Algunos sí'      },
      { val: 'no',      icon: '❌', label: 'No, ninguno'     },
    ].map(o => (
      <OptionCard key={o.val} selected={data.passport === o.val} onClick={() => set('passport', o.val)} className="mb-2">
        <span className="mr-2">{o.icon}</span>{o.label}
      </OptionCard>
    ))}
    {data.passport === 'no' && (
      <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-2xl p-3 text-sm text-yellow-800">
        📋 Si aún no tienen pasaporte, podemos orientarte con una guía gratuita para tramitarlo.
      </div>
    )}
    <NavButtons onBack={onBack} onNext={onNext} canNext={!!data.passport} />
  </>
);

// S2C: Sin visa
const Section2C = ({ data, set, onNext, onBack }) => (
  <>
    <SectionHeader
      emoji="💌"
      title="Por ahora no podemos cotizar"
      subtitle="Para viajar a EE. UU. se requiere visa aprobada. Pero podemos prepararte para cuando la tengas. 💫"
    />
    <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 text-sm text-red-800">
      🛑 Te enviaremos una <strong>guía gratuita</strong> para preparar tu entrevista de visa y consejos para planear tu futuro viaje mágico.
    </div>
    <div className="flex flex-col gap-4 mb-4">
      <FieldInput
        label="Nombre completo"
        placeholder="Tu nombre"
        value={data.noVisaName || ''}
        onChange={e => set('noVisaName', e.target.value)}
      />
      <FieldInput
        label="Correo electrónico"
        type="email"
        placeholder="tu@correo.com"
        value={data.noVisaEmail || ''}
        onChange={e => set('noVisaEmail', e.target.value)}
      />
    </div>
    <p className="font-semibold text-gray-700 mb-2 text-sm">¿Deseas recibir recordatorio cuando obtengas tu visa?</p>
    {[
      { val: 'yes', icon: '✅', label: 'Sí, avísame'   },
      { val: 'no',  icon: '❌', label: 'No, gracias'   },
    ].map(o => (
      <OptionCard key={o.val} selected={data.visaReminder === o.val} onClick={() => set('visaReminder', o.val)} className="mb-2">
        <span className="mr-2">{o.icon}</span>{o.label}
      </OptionCard>
    ))}
    <NavButtons
      onBack={onBack}
      onNext={onNext}
      nextLabel="Enviar y terminar ✨"
      canNext={!!data.noVisaName && !!data.noVisaEmail && !!data.visaReminder}
    />
  </>
);

// S3: Datos personales
const Section3 = ({ data, set, onNext, onBack }) => {
  const ok = data.fullName && data.email && data.whatsapp && data.city && data.departureCity;
  return (
    <>
      <SectionHeader emoji="🧳" title="Datos personales" subtitle="Para enviarte tu propuesta personalizada." />
      <div className="flex flex-col gap-4">
        <FieldInput label="Nombre completo del titular" placeholder="Nombre completo" value={data.fullName || ''} onChange={e => set('fullName', e.target.value)} />
        <FieldInput label="Correo electrónico" type="email" placeholder="tu@correo.com" value={data.email || ''} onChange={e => set('email', e.target.value)} />
        <FieldInput label="WhatsApp (con lada)" type="tel" placeholder="+52 33 1234 5678" value={data.whatsapp || ''} onChange={e => set('whatsapp', e.target.value)} />
        <FieldInput label="Ciudad y país de residencia" placeholder="Guadalajara, México" value={data.city || ''} onChange={e => set('city', e.target.value)} />
        <FieldInput label="Ciudad de salida" placeholder="Desde qué aeropuerto viajarías" value={data.departureCity || ''} onChange={e => set('departureCity', e.target.value)} />
      </div>
      <NavButtons onBack={onBack} onNext={onNext} canNext={ok} />
    </>
  );
};

// S4: Experiencia y detalles
const Section4 = ({ data, set, onNext, onBack }) => {
  const ok = data.experienceType && data.travelers && data.travelersAges && data.tripType;
  return (
    <>
      <SectionHeader emoji="🎢" title="Experiencia y detalles" />
      <div className="mb-6">
        <p className="font-semibold text-gray-700 mb-3 text-sm">¿Qué tipo de experiencia te interesa?</p>
        <div className="flex flex-col gap-2">
          {EXPERIENCE_TYPES.map(e => (
            <OptionCard key={e.id} selected={data.experienceType === e.id} onClick={() => set('experienceType', e.id)}>
              <span className="font-semibold">{e.label}</span>
              <span className="block text-xs text-gray-500 mt-0.5">{e.desc}</span>
            </OptionCard>
          ))}
        </div>
      </div>
      <div className="mb-4">
        <FieldInput
          label="¿Cuántas personas viajan (incluyéndote)?"
          type="number" min="1" placeholder="ej. 4"
          value={data.travelers || ''}
          onChange={e => set('travelers', e.target.value)}
        />
      </div>
      <div className="mb-4">
        <FieldInput
          label="Edades de los viajeros"
          placeholder="ej. 2 adultos, 2 niños de 7 y 10 años"
          value={data.travelersAges || ''}
          onChange={e => set('travelersAges', e.target.value)}
        />
      </div>
      <div className="mb-2">
        <p className="font-semibold text-gray-700 mb-2 text-sm">Tipo de viaje</p>
        <div className="flex flex-wrap gap-2">
          {TRIP_TYPES.map(t => (
            <button
              key={t}
              type="button"
              onClick={() => set('tripType', t)}
              className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all
                ${data.tripType === t
                  ? 'border-purple-500 bg-purple-100 text-purple-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-purple-300'}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <NavButtons onBack={onBack} onNext={onNext} canNext={ok} />
    </>
  );
};

// S5: Presupuesto
const Section5 = ({ data, set, onNext, onBack }) => {
  const ok = data.budget && data.advisoryLevel;
  return (
    <>
      <SectionHeader emoji="💰" title="Presupuesto y asesoría" />
      <div className="mb-6">
        <p className="font-semibold text-gray-700 mb-3 text-sm">Presupuesto aproximado por persona (USD):</p>
        <div className="flex flex-col gap-2">
          {BUDGETS.map(b => (
            <OptionCard key={b} selected={data.budget === b} onClick={() => set('budget', b)}>{b}</OptionCard>
          ))}
        </div>
      </div>
      <div className="mb-2">
        <p className="font-semibold text-gray-700 mb-3 text-sm">Nivel de asesoría deseado:</p>
        <div className="flex flex-col gap-2">
          {ADVISORY_LEVELS.map(a => (
            <OptionCard key={a.id} selected={data.advisoryLevel === a.id} onClick={() => set('advisoryLevel', a.id)}>
              <span className="font-semibold">{a.label}</span>
              <span className="block text-xs text-gray-500 mt-0.5">{a.desc}</span>
            </OptionCard>
          ))}
        </div>
      </div>
      <NavButtons onBack={onBack} onNext={onNext} canNext={ok} />
    </>
  );
};

// S6: Solo boletos (condicional)
const Section6 = ({ data, set, onNext, onBack }) => {
  const toggleTicket = (t) => {
    const curr = data.ticketTypes || [];
    set('ticketTypes', curr.includes(t) ? curr.filter(x => x !== t) : [...curr, t]);
  };
  const ok = (data.ticketTypes?.length > 0) && data.ticketDates && data.magicAdvisory;
  return (
    <>
      <SectionHeader emoji="🎟️" title="Detalles de boletos" subtitle="Cuéntanos qué tickets necesitas." />
      <div className="mb-5">
        <p className="font-semibold text-gray-700 mb-3 text-sm">¿Qué tipo de boletos necesitas? (puedes elegir varios)</p>
        <div className="flex flex-col gap-2">
          {TICKET_TYPES.map(t => (
            <OptionCard key={t} selected={(data.ticketTypes || []).includes(t)} onClick={() => toggleTicket(t)}>{t}</OptionCard>
          ))}
        </div>
      </div>
      <div className="mb-5">
        <FieldInput
          label="¿Qué fechas tienes en mente?"
          placeholder="ej. Del 15 al 22 de diciembre 2025"
          value={data.ticketDates || ''}
          onChange={e => set('ticketDates', e.target.value)}
        />
      </div>
      <div className="mb-2">
        <p className="font-semibold text-gray-700 mb-1 text-sm">¿Deseas agregar la Asesoría Mágica Personalizada?</p>
        <p className="text-xs text-gray-500 mb-3">Incluye itinerario optimizado, tips Genie+/Premier Access, reservaciones y estrategias para evitar filas.</p>
        {[
          { val: 'yes', label: '✅ Sí, quiero agregarla' },
          { val: 'no',  label: '❌ No, solo boletos'     },
        ].map(o => (
          <OptionCard key={o.val} selected={data.magicAdvisory === o.val} onClick={() => set('magicAdvisory', o.val)} className="mb-2">
            {o.label}
          </OptionCard>
        ))}
      </div>
      <NavButtons onBack={onBack} onNext={onNext} canNext={ok} />
    </>
  );
};

// S7: Expectativas
const Section7 = ({ data, set, onNext, onBack }) => {
  const ok = data.traveledBefore && data.excitement && data.howFound;
  return (
    <>
      <SectionHeader emoji="🌟" title="Expectativas y contacto final" />
      <div className="mb-5">
        <p className="font-semibold text-gray-700 mb-3 text-sm">¿Has viajado antes a Disney o Universal?</p>
        {[
          { val: 'yes', label: '✅ Sí, ya he ido'               },
          { val: 'no',  label: '🌟 No, sería mi primera vez'    },
        ].map(o => (
          <OptionCard key={o.val} selected={data.traveledBefore === o.val} onClick={() => set('traveledBefore', o.val)} className="mb-2">
            {o.label}
          </OptionCard>
        ))}
      </div>
      <div className="mb-5">
        <FieldTextarea
          label="¿Qué te gustaría vivir o experimentar en este viaje?"
          placeholder="Cuéntanos tus sueños y expectativas..."
          value={data.expectations || ''}
          onChange={e => set('expectations', e.target.value)}
        />
      </div>
      <div className="mb-5">
        <p className="font-semibold text-gray-700 mb-2 text-sm">¿Qué tan decidido(a) estás a realizarlo? (1 al 5)</p>
        <div className="flex gap-2 justify-center">
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              type="button"
              onClick={() => set('excitement', n)}
              className={`w-11 h-11 rounded-full border-2 font-bold text-base transition-all
                ${data.excitement === n
                  ? 'border-purple-500 bg-purple-500 text-white scale-110'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-purple-300'}`}
            >
              {n}
            </button>
          ))}
        </div>
        <div className="flex justify-between text-xs text-gray-400 mt-1 px-1">
          <span>Solo explorando</span><span>¡Listo para volar!</span>
        </div>
      </div>
      <div className="mb-5">
        <FieldInput
          label="¿Cuándo podrías hacer tu primer pago si la propuesta te convence?"
          placeholder="ej. En este mes, en 3 meses..."
          value={data.paymentTimeline || ''}
          onChange={e => set('paymentTimeline', e.target.value)}
        />
      </div>
      <div className="mb-2">
        <FieldInput
          label="¿Cómo nos conociste?"
          placeholder="Instagram, recomendación, Google..."
          value={data.howFound || ''}
          onChange={e => set('howFound', e.target.value)}
        />
      </div>
      <NavButtons onBack={onBack} onNext={onNext} nextLabel="Enviar cotización ✨" canNext={ok} />
    </>
  );
};

// Pantalla final
const FinalScreen = ({ noVisa, jsonData }) => {
  const dest = jsonData?.seccion1_destino;
  const docs = jsonData?.seccion2_documentacion;
  const personal = jsonData?.seccion3_datos_personales;
  const experiencia = jsonData?.seccion4_experiencia;
  const presupuesto = jsonData?.seccion5_presupuesto;
  const boletos = jsonData?.seccion6_boletos;
  const expectativas = jsonData?.seccion7_expectativas;
  const lead = jsonData?.lead_sin_visa;

  const passportLabel = { yes: '✅ Sí, todos', partial: '⚠️ Algunos sí', no: '❌ No' };
  const visaLabel     = { yes: '✅ Sí, todos', partial: '⚠️ Algunos sí', no: '❌ Sin visa' };
  const advisoryLabel = { full: 'Completa', basic: 'Básica', tickets: 'Solo boletos' };
  const experienceLabel = { full_pkg: 'Paquete completo', tickets: 'Solo entradas', cruise: 'Crucero Disney', other: 'Otro' };

  const Row = ({ label, value }) =>
    value ? (
      <div className="flex justify-between items-start gap-4 py-2 border-b border-gray-100 last:border-0">
        <span className="text-xs text-gray-400 font-medium shrink-0">{label}</span>
        <span className="text-sm text-gray-700 font-semibold text-right">{value}</span>
      </div>
    ) : null;

  const Card = ({ emoji, title, children }) => (
    <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-base">{emoji}</span>
        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{title}</span>
      </div>
      {children}
    </div>
  );

  return (
    <motion.div
      className="text-center"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <div className="text-5xl mb-3">{noVisa ? '💌' : '🎉'}</div>
      <h3 className="text-2xl font-bold text-gray-800 mb-2">
        {noVisa ? '¡Gracias por tu interés!' : '¡Gracias por tu confianza!'}
      </h3>
      <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
        {noVisa
          ? 'Cuando tengas tu visa aprobada, te ayudaremos a cumplir tu sueño. ¡Pronto recibirás nuestra guía! 💫'
          : 'Analizaremos tu información y te enviaremos una propuesta mágica y personalizada muy pronto. ✨'}
      </p>

      {/* Resumen */}
      <div className="text-left bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm mb-6">
        {/* Banner destino */}
        {dest && (
          <div className="bg-gradient-to-r from-pink-500 to-purple-600 px-5 py-4 text-white">
            <p className="text-xs font-semibold uppercase tracking-widest opacity-80 mb-0.5">Tu destino</p>
            <p className="text-xl font-bold">{dest.nombre}</p>
            <p className="text-sm opacity-80">{dest.ubicacion}</p>
          </div>
        )}

        <div className="p-5 flex flex-col gap-4">

          {/* Lead sin visa */}
          {noVisa && lead && (
            <Card emoji="👤" title="Tus datos">
              <Row label="Nombre"   value={lead.nombre} />
              <Row label="Correo"   value={lead.email}  />
              <Row label="Recordatorio de visa" value={lead.recordatorio === 'yes' ? '✅ Sí' : '❌ No'} />
            </Card>
          )}

          {/* Documentación */}
          {docs && (
            <Card emoji="🛂" title="Documentación">
              <Row label="Pasaporte" value={passportLabel[docs.pasaporte]} />
              {docs.visa && <Row label="Visa EE.UU." value={visaLabel[docs.visa]} />}
            </Card>
          )}

          {/* Datos personales */}
          {personal && (
            <Card emoji="👤" title="Datos de contacto">
              <Row label="Nombre"        value={personal.nombre}       />
              <Row label="Correo"        value={personal.email}        />
              <Row label="WhatsApp"      value={personal.whatsapp}     />
              <Row label="Residencia"    value={personal.ciudad}       />
              <Row label="Ciudad salida" value={personal.ciudadSalida} />
            </Card>
          )}

          {/* Experiencia */}
          {experiencia && (
            <Card emoji="🎢" title="Tu viaje">
              <Row label="Experiencia"  value={experienceLabel[experiencia.tipo]} />
              <Row label="Viajeros"     value={experiencia.viajeros ? `${experiencia.viajeros} persona(s)` : null} />
              <Row label="Edades"       value={experiencia.edades}    />
              <Row label="Tipo de viaje" value={experiencia.tipoViaje} />
            </Card>
          )}

          {/* Presupuesto */}
          {presupuesto && (
            <Card emoji="💰" title="Presupuesto y asesoría">
              <Row label="Presupuesto por persona" value={presupuesto.presupuesto}                    />
              <Row label="Nivel de asesoría"       value={advisoryLabel[presupuesto.nivelAsesoria]}   />
            </Card>
          )}

          {/* Boletos */}
          {boletos && (
            <Card emoji="🎟️" title="Boletos">
              <Row label="Tipos"           value={boletos.tiposBoletos?.join(', ')}                       />
              <Row label="Fechas"          value={boletos.fechas}                                          />
              <Row label="Asesoría mágica" value={boletos.asesoriamagica === 'yes' ? '✅ Sí' : '❌ No'}   />
            </Card>
          )}

          {/* Expectativas */}
          {expectativas && (
            <Card emoji="🌟" title="Expectativas">
              <Row label="¿Ha viajado antes?"  value={expectativas.viajoAntes === 'yes' ? '✅ Sí' : '🌟 Primera vez'} />
              <Row label="Nivel de decisión"   value={expectativas.nivelDecision ? `${expectativas.nivelDecision} / 5` : null} />
              <Row label="Primer pago"         value={expectativas.pagoEstimado}    />
              <Row label="¿Cómo nos conoció?"  value={expectativas.comoNosConocio}  />
              {expectativas.expectativas && (
                <div className="pt-2 mt-1 border-t border-gray-100">
                  <p className="text-xs text-gray-400 font-medium mb-1">Lo que espera vivir</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{expectativas.expectativas}</p>
                </div>
              )}
            </Card>
          )}

        </div>
      </div>

      {/* Footer mágico */}
      <div className="inline-block bg-gradient-to-r from-pink-100 to-purple-100 border border-purple-200 rounded-2xl px-6 py-3 text-purple-700 font-semibold text-sm">
        🐭 ¡El mundo mágico te espera!
      </div>
    </motion.div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────

const QuoteForm = () => {
  const [step, setStep]       = useState(1);
  const [done, setDone]       = useState(false);
  const [noVisa, setNoVisa]   = useState(false);
  const [savedJSON, setSavedJSON] = useState(null);
  const [data, setData]       = useState({});

  const set = (key, val) => setData(prev => ({ ...prev, [key]: val }));

  const dest        = DESTINATIONS.find(d => d.id === data.destination);
  const region      = dest?.region;
  const visaNo      = data.visa === 'no';
  const ticketsOnly = data.advisoryLevel === 'tickets';

  const buildJSON = (isNoVisa = false) => {
    return {
      meta: {
        submittedAt: new Date().toISOString(),
        formVersion: '2.0',
        tipo: isNoVisa ? 'lead_sin_visa' : 'cotizacion_completa',
      },
      seccion1_destino: dest ? {
        id:        dest.id,
        nombre:    dest.label,
        ubicacion: dest.sublabel,
        region:    dest.region,
      } : null,
      seccion2_documentacion: {
        pasaporte:    data.passport     ?? null,
        visa:         data.visa         ?? null,
        avisoVisa:    data.visaReminder ?? null,
      },
      ...(isNoVisa ? {
        lead_sin_visa: {
          nombre:    data.noVisaName  ?? null,
          email:     data.noVisaEmail ?? null,
          recordatorio: data.visaReminder ?? null,
        },
      } : {
        seccion3_datos_personales: {
          nombre:        data.fullName      ?? null,
          email:         data.email         ?? null,
          whatsapp:      data.whatsapp      ?? null,
          ciudad:        data.city          ?? null,
          ciudadSalida:  data.departureCity ?? null,
        },
        seccion4_experiencia: {
          tipo:        data.experienceType ?? null,
          viajeros:    data.travelers      ?? null,
          edades:      data.travelersAges  ?? null,
          tipoViaje:   data.tripType       ?? null,
        },
        seccion5_presupuesto: {
          presupuesto:    data.budget        ?? null,
          nivelAsesoria:  data.advisoryLevel ?? null,
        },
        seccion6_boletos: ticketsOnly ? {
          tiposBoletos:   data.ticketTypes   ?? [],
          fechas:         data.ticketDates   ?? null,
          asesoriamagica: data.magicAdvisory ?? null,
        } : null,
        seccion7_expectativas: {
          viajoAntes:     data.traveledBefore  ?? null,
          expectativas:   data.expectations    ?? null,
          nivelDecision:  data.excitement      ?? null,
          pagoEstimado:   data.paymentTimeline ?? null,
          comoNosConocio: data.howFound        ?? null,
        },
      }),
    };
  };

  const progressStep = () => {
    if (step === 1)                    return 1;
    if (step === '2a' || step === '2b' || step === '2c') return 2;
    if (step === 3)                    return 3;
    if (step === 4)                    return 4;
    if (step === 5)                    return 5;
    if (step === 6)                    return 6;
    return 7;
  };

  const handleNext = () => {
    if (step === 1) {
      setStep(region === 'eu' ? '2b' : '2a');
    } else if (step === '2a') {
      visaNo ? setStep('2c') : setStep(3);
    } else if (step === '2b') {
      setStep(3);
    } else if (step === '2c') {
      const json = buildJSON(true);
      setSavedJSON(json);
      console.log('📋 Lead sin visa:', JSON.stringify(json, null, 2));
      setNoVisa(true);
      setDone(true);
    } else if (step === 3) {
      setStep(4);
    } else if (step === 4) {
      setStep(5);
    } else if (step === 5) {
      ticketsOnly ? setStep(6) : setStep(7);
    } else if (step === 6) {
      setStep(7);
    } else if (step === 7) {
      const json = buildJSON(false);
      setSavedJSON(json);
      console.log('📋 Cotización completa:', JSON.stringify(json, null, 2));
      setDone(true);
    }
  };

  const handleBack = () => {
    if (step === '2a' || step === '2b') setStep(1);
    else if (step === '2c')             setStep('2a');
    else if (step === 3)                setStep(region === 'eu' ? '2b' : '2a');
    else if (step === 4)                setStep(3);
    else if (step === 5)                setStep(4);
    else if (step === 6)                setStep(5);
    else if (step === 7)                setStep(ticketsOnly ? 6 : 5);
  };

  return (
    <section id="quote" className="py-20 bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <motion.h2
          className="text-3xl sm:text-4xl font-bold text-center mb-2 bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Cotiza tu Viaje Mágico 🪄
        </motion.h2>
        <p className="text-center text-gray-500 text-sm mb-8">
          Completa el formulario y te enviamos una propuesta personalizada
        </p>

        <motion.div
          className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 relative overflow-hidden"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          {/* Decoración */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-pink-100 to-purple-100 rounded-bl-full opacity-50 pointer-events-none" />

          {!done && <ProgressBar step={progressStep()} total={7} />}

          <AnimatePresence mode="wait">
            <motion.div
              key={String(step) + String(done)}
              variants={sectionVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              {done ? (
                <FinalScreen noVisa={noVisa} jsonData={savedJSON} />
              ) : step === 1 ? (
                <Section1  data={data} set={set} onNext={handleNext} />
              ) : step === '2a' ? (
                <Section2A data={data} set={set} onNext={handleNext} onBack={handleBack} />
              ) : step === '2b' ? (
                <Section2B data={data} set={set} onNext={handleNext} onBack={handleBack} />
              ) : step === '2c' ? (
                <Section2C data={data} set={set} onNext={handleNext} onBack={handleBack} />
              ) : step === 3 ? (
                <Section3  data={data} set={set} onNext={handleNext} onBack={handleBack} />
              ) : step === 4 ? (
                <Section4  data={data} set={set} onNext={handleNext} onBack={handleBack} />
              ) : step === 5 ? (
                <Section5  data={data} set={set} onNext={handleNext} onBack={handleBack} />
              ) : step === 6 ? (
                <Section6  data={data} set={set} onNext={handleNext} onBack={handleBack} />
              ) : step === 7 ? (
                <Section7  data={data} set={set} onNext={handleNext} onBack={handleBack} />
              ) : null}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default QuoteForm;